import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { spinnerRing } from '../utils/bookingUtils';
import { formatMoney } from '../utils/currencyUtil';
import { formatPlanDuration } from '../utils/subscriptionDateUtil';
import { fetchSignupReceipt, setSignupRequestStatus } from '../utils/posSignupUtil';
import { describeEmailResult, sendSignupCompletedEmail } from '../utils/emailServiceUtil';
import { describeWriteError } from '../utils/firebaseErrorUtil';
import {
  POS_SIGNUP_FIELD_MAX, POS_SIGNUP_STATUS, POS_SIGNUP_STATUS_LABELS, POS_SIGNUP_TYPES, POS_SIGNUP_TYPE_LABELS,
} from '../constants/posConst';
import '../css/Pos.css';
import '../css/PosAdmin.css';
import '../css/PosSubscription.css';

/**
 * One sign-up request, opened from /admin -> Sign-up Requests: who, what they
 * asked for, what they paid (with the receipt) or when they want their call,
 * and the buttons that close it out.
 *
 * Marking a request Done or Dismissed changes nothing else — the client is
 * registered by hand on Client Registration, exactly as before. The note is
 * kept on the request for whoever looks at it next.
 *
 * Closes on the × or Close only — never on a backdrop tap.
 *
 * @param {object} props
 * @param {object|null} props.request - null renders nothing
 * @param {object} props.config - for the currency symbol
 * @param {string} props.signedInEmail
 * @param {() => void} props.onClose
 * @param {(result: {type: string, title: string, message: string}) => void} props.onDone
 * @returns {JSX.Element|null}
 */
function PosSignupReviewModal({ request, config, signedInEmail, onClose, onDone }) {
  const [receipt, setReceipt] = useState('');
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const requestId = request?.id || '';
  const isSubscribe = request?.type === POS_SIGNUP_TYPES.SUBSCRIBE;
  const isDemo = request?.type === POS_SIGNUP_TYPES.DEMO;
  const savedNote = request?.adminNote || '';

  // Loaded per request: the list never downloads the receipt photos.
  useEffect(() => {
    if (!requestId) return undefined;
    let active = true;
    setReceipt('');
    setError('');
    setBusy(false);
    if (!isSubscribe) {
      setReceiptLoading(false);
      return undefined;
    }
    setReceiptLoading(true);
    fetchSignupReceipt(requestId)
      .then((url) => { if (active) setReceipt(url); })
      .catch((loadError) => {
        console.error('Could not load sign-up receipt:', loadError);
        if (active) setError('The receipt could not be loaded.');
      })
      .finally(() => { if (active) setReceiptLoading(false); });
    return () => { active = false; };
  }, [requestId, isSubscribe]);

  // The note field starts from whatever was saved last time.
  useEffect(() => {
    setNote(savedNote);
  }, [requestId, savedNote]);

  if (!request) return null;

  /**
   * Moves the request to a new status.
   *
   * @param {string} status
   * @returns {Promise<void>}
   */
  const handleStatus = async (status) => {
    setBusy(true);
    setError('');
    try {
      await setSignupRequestStatus(request, status, note, signedInEmail);
      const messages = {
        [POS_SIGNUP_STATUS.DONE]: `${request.business} is marked done.`,
        [POS_SIGNUP_STATUS.DISMISSED]: `${request.business} is dismissed. It stays under Dismissed if you need it again.`,
        [POS_SIGNUP_STATUS.PENDING]: `${request.business} is back under New.`,
      };
      // Only Done tells the visitor anything. Awaited so the result dialog
      // can say whether the email went out; the status is already saved.
      let emailLine = '';
      let emailFailed = false;
      if (status === POS_SIGNUP_STATUS.DONE) {
        const emailResult = await sendSignupCompletedEmail(request);
        emailLine = describeEmailResult(emailResult, request.email);
        emailFailed = !emailResult.success && !emailResult.skipped;
      }
      onDone({
        type: emailFailed ? 'warning' : 'success',
        title: 'Request updated',
        message: emailLine ? `${messages[status]}\n\n${emailLine}` : messages[status],
      });
    } catch (saveError) {
      console.error('Updating sign-up request failed:', saveError);
      setError(saveError.isUserFacing
        ? saveError.message
        : describeWriteError(saveError, { what: 'this request', path: 'posSignupRequests' }));
      setBusy(false);
    }
  };

  const pending = request.status === POS_SIGNUP_STATUS.PENDING;

  return createPortal(
    <div className="pos-modal-overlay">
      <div className="pos-modal ps-review" role="dialog" aria-modal="true" aria-label="Review sign-up request">
        <header className="pos-modal-head">
          <h3>{POS_SIGNUP_TYPE_LABELS[request.type] || 'Request'} request</h3>
          <button type="button" className="pos-modal-close pos-modal-close-text" onClick={onClose} disabled={busy} aria-label="Close">&times;</button>
        </header>

        <div className="pos-modal-body">
          <dl className="ps-summary">
            <div><dt>Status</dt><dd>{POS_SIGNUP_STATUS_LABELS[request.status] || request.status}</dd></div>
            <div><dt>Business</dt><dd>{request.business}</dd></div>
            <div><dt>Name</dt><dd>{request.name}</dd></div>
            <div><dt>Email</dt><dd><a href={`mailto:${request.email}`}>{request.email}</a></dd></div>
            <div><dt>Contact</dt><dd><a href={`tel:${request.phone}`}>{request.phone}</a></dd></div>
            <div>
              <dt>Plan</dt>
              <dd>
                {request.planName || '—'}
                {request.planName && !request.isCustom && ` · ${formatPlanDuration(request.durationValue, request.durationUnit)}`}
              </dd>
            </div>

            {isSubscribe ? (
              <>
                <div><dt>Plan price</dt><dd>{formatMoney(request.planPrice, config)}</dd></div>
                <div><dt>Free staff</dt><dd>{request.freeStaff || 0}</dd></div>
                {(request.staffEmails || []).length > 0 && (
                  <div>
                    <dt>Staff accounts</dt>
                    <dd>
                      <ul className="ps-email-list">
                        {request.staffEmails.map((staffEmail, index) => (
                          <li key={staffEmail}>
                            {staffEmail}
                            {index < (request.freeStaff || 0) ? ' (free)' : ''}
                          </li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                )}
                <div>
                  <dt>Extra staff</dt>
                  <dd>
                    {request.extraStaff > 0
                      ? `${request.extraStaff} × ${formatMoney(request.extraStaffPrice, config)} = ${formatMoney(request.extraStaffTotal, config)}`
                      : 'None'}
                  </dd>
                </div>
                <div>
                  <dt>Warehouse</dt>
                  <dd>{request.warehouse > 0 ? formatMoney(request.warehouseTotal, config) : 'None'}</dd>
                </div>
                <div className="ps-summary-total"><dt>Total paid</dt><dd>{formatMoney(request.total, config)}</dd></div>
                <div><dt>Paid with</dt><dd>{request.payApp || '—'}</dd></div>
              </>
            ) : isDemo && (
              <>
                <div><dt>Call</dt><dd>{request.callDate} · {request.slotLabel} (PH time)</dd></div>
                <div><dt>Business does</dt><dd>{request.about}</dd></div>
                <div><dt>Wants</dt><dd>{request.features}</dd></div>
              </>
            )}

            <div><dt>Sent</dt><dd>{new Date(request.submittedAt).toLocaleString()}</dd></div>
            {request.reviewedBy && (
              <div><dt>Last update</dt><dd>{new Date(request.reviewedAt).toLocaleString()} by {request.reviewedBy}</dd></div>
            )}
          </dl>

          {isSubscribe && (
            <div className="ps-receipt">
              <span className="pa-field-label">Proof of payment</span>
              {receiptLoading ? (
                <div className="pa-loading pa-loading-inline">{spinnerRing('Loading receipt')}</div>
              ) : receipt ? (
                <a href={receipt} target="_blank" rel="noreferrer" className="ps-receipt-link">
                  <img src={receipt} alt="Payment receipt" />
                </a>
              ) : (
                <p className="pa-field-hint">No receipt image was found for this request.</p>
              )}
            </div>
          )}

          <label className="pa-field">
            <span className="pa-field-label">Note</span>
            <textarea
              className="pa-input pa-textarea"
              rows={2}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              maxLength={POS_SIGNUP_FIELD_MAX.note}
              placeholder="Registered as /juans-cafe, login emailed."
              disabled={busy}
            />
            <span className="pa-field-hint">Saved with the request when you change its status.</span>
          </label>

          {busy && <div className="pa-loading pa-loading-inline">{spinnerRing('Saving')}</div>}
          {error && <p className="pa-role-error">{error}</p>}
        </div>

        <footer className="pos-modal-foot">
          {pending ? (
            <>
              <button type="button" className="pa-btn ps-btn-danger" onClick={() => handleStatus(POS_SIGNUP_STATUS.DISMISSED)} disabled={busy}>Dismiss</button>
              <span className="pos-foot-spacer" />
              <button type="button" className="pa-btn pa-btn-quiet" onClick={onClose} disabled={busy}>Close</button>
              <button type="button" className="pa-btn pa-btn-primary" onClick={() => handleStatus(POS_SIGNUP_STATUS.DONE)} disabled={busy}>Mark done</button>
            </>
          ) : (
            <>
              <button type="button" className="pa-btn pa-btn-quiet" onClick={() => handleStatus(POS_SIGNUP_STATUS.PENDING)} disabled={busy}>Move back to New</button>
              <span className="pos-foot-spacer" />
              <button type="button" className="pa-btn pa-btn-quiet" onClick={onClose} disabled={busy}>Close</button>
            </>
          )}
        </footer>
      </div>
    </div>,
    document.body,
  );
}

export default PosSignupReviewModal;
